import { FC } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useForm, Controller } from "react-hook-form";
import { ScrollArea } from "@/components/ui/scroll-area";

interface ExpenseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ExpenseFormData {
  name: string;
  amount: number;
  type: 'fixed' | 'variable';
  category: string;
  description?: string;
  due_date?: string;
  payment_date?: string;
  payment_reference?: string;
  is_paid: boolean;
}

export const ExpenseDialog: FC<ExpenseDialogProps> = ({ open, onOpenChange }) => {
  const queryClient = useQueryClient();
  const { register, handleSubmit, control, reset, formState: { isSubmitting } } = useForm<ExpenseFormData>();

  const createExpense = useMutation({
    mutationFn: async (data: ExpenseFormData) => {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('Error getting user:', userError);
        throw userError;
      }

      if (!user) {
        throw new Error('No authenticated user found');
      }

      console.log('Creating expense with data:', data);
      const { error } = await supabase
        .from('platform_expenses')
        .insert([{
          ...data,
          created_by: user.id
        }]);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Gasto agregado correctamente');
      queryClient.invalidateQueries({ queryKey: ['platform-expenses'] });
      queryClient.invalidateQueries({ queryKey: ['financial-summary'] });
      onOpenChange(false);
      reset();
    },
    onError: (error) => {
      console.error('Error creating expense:', error);
      toast.error('Error al crear el gasto');
    },
  });

  const onSubmit = (data: ExpenseFormData) => {
    createExpense.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-full sm:max-w-[600px] max-h-[90vh] p-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle>Agregar Nuevo Gasto</DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-120px)] px-6 pb-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                {...register('name', { required: true })}
                placeholder="Nombre del gasto"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount">Monto</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                {...register('amount', { required: true, min: 0 })}
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">Tipo</Label>
              <Controller
                name="type"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona el tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fijo</SelectItem>
                      <SelectItem value="variable">Variable</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Categoría</Label>
              <Controller
                name="category"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona la categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hosting">Hosting</SelectItem>
                      <SelectItem value="software">Software</SelectItem>
                      <SelectItem value="marketing">Marketing</SelectItem>
                      <SelectItem value="salaries">Salarios</SelectItem>
                      <SelectItem value="other">Otros</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="due_date">Fecha de vencimiento</Label>
              <Input
                id="due_date"
                type="date"
                {...register('due_date')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment_date">Fecha de pago</Label>
              <Input
                id="payment_date"
                type="date"
                {...register('payment_date')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment_reference">Referencia de pago</Label>
              <Input
                id="payment_reference"
                {...register('payment_reference')}
                placeholder="Número de factura o referencia"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                {...register('description')}
                placeholder="Descripción del gasto"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 sticky bottom-0 bg-background">
              <Button variant="outline" onClick={() => onOpenChange(false)} type="button">
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};